import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { api, handleApiError } from "../utils/api";
import {
  fetchPendingInvite,
  fetchPushStatus,
  ackPendingInvite,
} from "../utils/push";

const AuthContext = createContext();

const normalizeCongregazioneInfo = (value) => {
  if (!value) return null;
  return {
    id: value.id,
    codice: value.codice,
    nome: value.nome,
    servizio_speciale: Boolean(value.servizio_speciale),
    rapporto_servizio_attivo: Boolean(
      value.rapporto_servizio_attivo ?? value.congregazione_rapporto_servizio_attivo
    ),
    gruppi_servizio_visibili: Boolean(
      value.gruppi_servizio_visibili ??
        value.congregazione_gruppi_servizio_visibili
    ),
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve essere usato all'interno di un AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeCongregazione, setActiveCongregazione] = useState(() => {
    try {
      const stored = localStorage.getItem("activeCongregazione");
      return stored ? normalizeCongregazioneInfo(JSON.parse(stored)) : null;
    } catch (_error) {
      return null;
    }
  });
  const [delegheInfo, setDelegheInfo] = useState({ primaria: null, deleghe: [] });
  const [delegheLoading, setDelegheLoading] = useState(false);
  const [pushStatus, setPushStatus] = useState({ subscriptions: 0 });
  const [pushInvite, setPushInvite] = useState({
    pending: false,
    message: null,
  });

  const originalFetchRef = useRef(window.fetch);

  const applyCongregazioneHeader = useCallback((congregazione) => {
    if (congregazione?.id) {
      axios.defaults.headers.common["X-Congregazione-Id"] = congregazione.id;
    } else {
      delete axios.defaults.headers.common["X-Congregazione-Id"];
    }
  }, []);

  useEffect(() => {
    applyCongregazioneHeader(activeCongregazione);
  }, [activeCongregazione, applyCongregazioneHeader]);

  useEffect(() => {
    const originalFetch = originalFetchRef.current;

    const patchedFetch = (input, init = {}) => {
      const newInit = { ...init };
      const headers = new Headers(init?.headers || {});

      if (activeCongregazione?.id) {
        headers.set("X-Congregazione-Id", activeCongregazione.id);
      } else {
        headers.delete("X-Congregazione-Id");
      }

      newInit.headers = headers;
      return originalFetch(input, newInit);
    };

    window.fetch = patchedFetch;

    return () => {
      window.fetch = originalFetch;
    };
  }, [activeCongregazione]);

  const updateActiveCongregazione = useCallback(
    (congregazione) => {
      const normalized = normalizeCongregazioneInfo(congregazione);
      setActiveCongregazione(normalized);
      applyCongregazioneHeader(normalized);
      if (normalized) {
        localStorage.setItem(
          "activeCongregazione",
          JSON.stringify(normalized)
        );
      } else {
        localStorage.removeItem("activeCongregazione");
      }
    },
    [applyCongregazioneHeader]
  );

  const fetchDeleghe = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setDelegheInfo({ primaria: null, deleghe: [] });
      setDelegheLoading(false);
      return;
    }
    setDelegheLoading(true);
    try {
      const response = await api.get("/deleghe/mie");
      const primaria =
        response.data?.primaria ||
        (user?.congregazione_id
          ? {
              id: user.congregazione_id,
              codice: user.congregazione_codice,
              nome: user.congregazione_nome,
              servizio_speciale:
                user.congregazione_servizio_speciale ?? false,
              rapporto_servizio_attivo:
                user.congregazione_rapporto_servizio_attivo ?? false,
              gruppi_servizio_visibili:
                user.congregazione_gruppi_servizio_visibili ?? false,
            }
          : null);
      const deleghe = Array.isArray(response.data?.deleghe)
        ? response.data.deleghe
        : [];
      const normalizedPrimaria = normalizeCongregazioneInfo(primaria);

      const allowedCongregazioni = new Set();
      if (user?.ruolo !== "super_admin") {
        if (normalizedPrimaria?.id) {
          allowedCongregazioni.add(normalizedPrimaria.id);
        }
        deleghe.forEach((delega) => {
          if (delega?.congregazione_id) {
            allowedCongregazioni.add(delega.congregazione_id);
          }
        });
      }

      setDelegheInfo({ primaria: normalizedPrimaria, deleghe });
      if (!activeCongregazione?.id && normalizedPrimaria) {
        updateActiveCongregazione(normalizedPrimaria);
      } else if (
        activeCongregazione?.id &&
        user?.ruolo !== "super_admin" &&
        !allowedCongregazioni.has(activeCongregazione.id)
      ) {
        toast.error(
          "La congregazione attiva non è più disponibile. Torno a quella principale."
        );
        if (normalizedPrimaria) {
          updateActiveCongregazione(normalizedPrimaria);
        } else {
          updateActiveCongregazione(null);
        }
      }
    } catch (error) {
      console.error("Errore nel caricamento delle deleghe:", error);
    } finally {
      setDelegheLoading(false);
    }
  }, [
    isAuthenticated,
    user,
    activeCongregazione?.id,
    updateActiveCongregazione,
  ]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (user.ruolo === "super_admin") {
      if (user.ruolo_base && user.ruolo !== "super_admin") {
        setUser({ ...user, ruolo: "super_admin", ruolo_base: "super_admin" });
      }
      return;
    }

    const baseRole = user.ruolo_base || user.ruolo;
    let effectiveRole = baseRole;

    if (
      activeCongregazione?.id &&
      user.congregazione_id &&
      activeCongregazione.id !== user.congregazione_id
    ) {
      const delega = (delegheInfo?.deleghe || []).find(
        (item) => item.congregazione_id === activeCongregazione.id
      );
      if (delega?.ruolo_delega) {
        effectiveRole = delega.ruolo_delega;
      }
    }

    if (user.ruolo !== effectiveRole || user.ruolo_base !== baseRole) {
      setUser({ ...user, ruolo: effectiveRole, ruolo_base: baseRole });
    }
  }, [user, activeCongregazione?.id, delegheInfo?.deleghe]);

  // Verifica del token all'avvio
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const syncActiveCongregazioneWithUser = useCallback(
    (userData, preserveExisting = false) => {
      if (!userData) {
        updateActiveCongregazione(null);
        return;
      }

      if (userData.ruolo === "super_admin") {
        if (preserveExisting && activeCongregazione?.id) {
          updateActiveCongregazione(activeCongregazione);
        } else {
          try {
            const stored = localStorage.getItem("activeCongregazione");
            if (stored) {
              updateActiveCongregazione(JSON.parse(stored));
              return;
            }
          } catch (_error) {
            // ignore
          }
          updateActiveCongregazione(null);
        }
      } else {
        if (preserveExisting && activeCongregazione?.id) {
          updateActiveCongregazione(activeCongregazione);
        } else {
          updateActiveCongregazione({
            id: userData.congregazione_id,
            codice: userData.congregazione_codice,
            nome: userData.congregazione_nome,
            servizio_speciale:
              userData.congregazione_servizio_speciale ?? false,
            rapporto_servizio_attivo:
              userData.congregazione_rapporto_servizio_attivo ?? false,
            gruppi_servizio_visibili:
              userData.congregazione_gruppi_servizio_visibili ?? false,
          });
        }
      }
    },
    [activeCongregazione, updateActiveCongregazione]
  );

  const loadPushMeta = useCallback(async () => {
    // Carica stato subscription e inviti pending
    try {
      const [statusRes, inviteRes] = await Promise.allSettled([
        fetchPushStatus(),
        fetchPendingInvite(),
      ]);
      if (statusRes.status === "fulfilled") {
        setPushStatus(statusRes.value || { subscriptions: 0 });
      }
      if (inviteRes.status === "fulfilled") {
        setPushInvite(inviteRes.value || { pending: false, message: null });
      }
    } catch (error) {
      // non bloccare il flusso di auth per errori push
      console.error("Errore caricamento meta push:", error);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await api.get("/auth/verify");
      setUser(response.data.user);
      setIsAuthenticated(true);
      syncActiveCongregazioneWithUser(response.data.user, true);
      loadPushMeta();
    } catch (error) {
      // Rimuovi il token senza mostrare toast durante la verifica iniziale
      localStorage.removeItem("token");
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identificatore, password, congregazioneCodice) => {
    try {
      const payload = {
        identificatore: identificatore?.trim(),
        password,
      };

      if (congregazioneCodice) {
        payload.congregazione_codice = congregazioneCodice.padStart(3, "0");
      }

      const response = await axios.post("/auth/login", payload);
      const {
        token,
        user,
        active_congregazione,
        ruolo_congregazione,
      } = response.data;
      const baseRole = user?.ruolo;
      const effectiveRole = ruolo_congregazione || baseRole;
      const userWithRole = {
        ...user,
        ruolo: effectiveRole,
        ruolo_base: baseRole || user?.ruolo_base,
      };

      localStorage.setItem("token", token);

      setUser(userWithRole);
      setIsAuthenticated(true);
      if (active_congregazione) {
        updateActiveCongregazione(active_congregazione);
      } else {
        syncActiveCongregazioneWithUser(userWithRole);
      }
      loadPushMeta();

      toast.success("Accesso effettuato con successo!");
      return { success: true, user: userWithRole };
    } catch (error) {
      const message =
        error.response?.data?.message || "Errore durante l'accesso";
      const remainingAttempts = error.response?.data?.remaining_attempts;
      const remainingMinutes = error.response?.data?.remaining_minutes;
      const statusCode = error.response?.status;
      toast.error(message);
      return {
        success: false,
        error: message,
        remainingAttempts,
        remainingMinutes,
        statusCode,
      };
    }
  };

  const logout = (showToast = true) => {
    localStorage.removeItem("token");
    localStorage.removeItem("activeCongregazione");
    setUser(null);
    setIsAuthenticated(false);
    updateActiveCongregazione(null);
    if (showToast) {
      toast.success("Logout effettuato con successo");
    }
  };

  const register = async (userData) => {
    try {
      const _response = await axios.post("/auth/register", userData);
      toast.success("Registrazione completata con successo!");
      return { success: true };
    } catch (error) {
      const message =
        error.response?.data?.message || "Errore durante la registrazione";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateProfile = async (userData) => {
    try {
      const response = await api.put("/auth/profile", userData);
      setUser(response.data.user);
      toast.success("Profilo aggiornato con successo!");
      return { success: true };
    } catch (error) {
      const result = handleApiError(error, logout);
      if (result.error === "Sessione scaduta") {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
      } else {
        toast.error(result.error);
      }
      return { success: false, error: result.error };
    }
  };

  const acceptPrivacyPolicy = async () => {
    try {
      const response = await api.post("/auth/privacy-accept");
      const acceptedAt =
        response.data?.accepted_at || new Date().toISOString();
      setUser((prev) =>
        prev ? { ...prev, privacy_policy_accepted_at: acceptedAt } : prev
      );
      toast.success("Grazie per aver accettato la privacy policy.");
      return { success: true, accepted_at: acceptedAt };
    } catch (error) {
      const result = handleApiError(error, logout);
      toast.error(
        result.error || "Errore durante il salvataggio del consenso privacy"
      );
      return { success: false, error: result.error };
    }
  };

  const revokePrivacyPolicy = async () => {
    try {
      await api.post("/auth/privacy-revoke");
      setUser((prev) =>
        prev ? { ...prev, privacy_policy_accepted_at: null } : prev
      );
      return { success: true };
    } catch (error) {
      const result = handleApiError(error, logout);
      toast.error(
        result.error || "Errore durante la revoca del consenso privacy"
      );
      return { success: false, error: result.error };
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDeleghe();
    } else {
      setDelegheInfo({ primaria: null, deleghe: [] });
    }
  }, [isAuthenticated, fetchDeleghe]);

  const updateRapportoServizioFlag = useCallback((enabled, congregationId = null) => {
    const matchesCongregazione = (valueId, targetId) => {
      if (valueId == null || targetId == null) return false;
      return Number(valueId) === Number(targetId);
    };

    setUser((prev) => {
      if (!prev) return prev;
      if (congregationId && !matchesCongregazione(prev.congregazione_id, congregationId)) {
        return prev;
      }
      return { ...prev, congregazione_rapporto_servizio_attivo: enabled };
    });
    setActiveCongregazione((prev) => {
      if (!prev) return prev;
      if (congregationId && !matchesCongregazione(prev.id, congregationId)) {
        return prev;
      }
      const next = { ...prev, rapporto_servizio_attivo: enabled };
      localStorage.setItem("activeCongregazione", JSON.stringify(next));
      return next;
    });
    setDelegheInfo((prev) => {
      if (!prev || !congregationId) return prev;
      const primaria =
        matchesCongregazione(prev.primaria?.id, congregationId)
          ? {
              ...prev.primaria,
              rapporto_servizio_attivo: enabled,
              congregazione_rapporto_servizio_attivo: enabled,
            }
          : prev.primaria;
      const deleghe = Array.isArray(prev.deleghe)
        ? prev.deleghe.map((delega) =>
            matchesCongregazione(delega?.congregazione_id, congregationId)
              ? {
                  ...delega,
                  rapporto_servizio_attivo: enabled,
                  congregazione_rapporto_servizio_attivo: enabled,
                }
              : delega
          )
        : prev.deleghe;
      return { ...prev, primaria, deleghe };
    });
  }, []);

  const updateGruppiServizioVisibilita = useCallback((enabled) => {
    setUser((prev) =>
      prev
        ? { ...prev, congregazione_gruppi_servizio_visibili: enabled }
        : prev
    );
    setActiveCongregazione((prev) =>
      prev ? { ...prev, gruppi_servizio_visibili: enabled } : prev
    );
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    activeCongregazione,
    setActiveCongregazione: updateActiveCongregazione,
    login,
    logout,
    register,
    updateProfile,
    delegheInfo,
    delegheLoading,
    refreshDeleghe: fetchDeleghe,
    acceptPrivacyPolicy,
    revokePrivacyPolicy,
    updateRapportoServizioFlag,
    updateGruppiServizioVisibilita,
    pushStatus,
    pushInvite,
    ackPushInvite: async () => {
      await ackPendingInvite();
      setPushInvite({ pending: false, message: null });
    },
    refreshPushStatus: async () => {
      const status = await fetchPushStatus();
      setPushStatus(status || { subscriptions: 0 });
      return status || { subscriptions: 0 };
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
